/**
 * Money formatting for every currency the estate actually trades in.
 *
 * `Intl` decides the symbol *and* its placement, which is why this doesn't
 * expose a bare symbol to concatenate: a third of our non-GBP shops are on
 * currencies where a `£`-style prefix is simply wrong — SEK/NOK/DKK render as
 * `kr 5`, RON as `lei 5`, PLN as `zł 5`. Building the string by hand gets
 * those silently wrong.
 *
 * `narrowSymbol` is deliberate too: the default display gives `US$5` / `CA$5`
 * / `A$5`, which reads as foreign to the retailer whose own currency it is.
 * In-app a shop only ever sees its own currency, so the bare `$` is
 * unambiguous in context.
 *
 * Locale stays `en-GB` for everyone — the dashboard is English-only.
 *
 * This is the single money formatter for the app. `lib/liaInsights.jsx`
 * delegates here rather than keeping its own: it used to build the same
 * formatters without `narrowSymbol`, which rendered `US$45.00` in the
 * Clicks-to-sales card directly beneath `$0.50` in the CPC tile on the very
 * same Ads page.
 */

const LOCALE = 'en-GB';
const DEFAULT_CURRENCY = 'GBP';

// Intl.NumberFormat construction is measurable when a table renders tens of
// rows, so formatters are cached per currency + precision.
const formatters = new Map();

const buildFormatter = (code, decimals) => {
	const options = {
		style: 'currency',
		currency: code,
		currencyDisplay: 'narrowSymbol',
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals
	};
	try {
		return new Intl.NumberFormat(LOCALE, options);
	} catch {
		// `narrowSymbol` is ES2020; fall back to the wide symbol rather than
		// dropping the currency entirely on an older engine.
		try {
			return new Intl.NumberFormat(LOCALE, { ...options, currencyDisplay: 'symbol' });
		} catch {
			// A malformed code (not three letters) throws whatever we ask for.
			return null;
		}
	}
};

const formatterFor = (currency, decimals) => {
	const code = String(currency || DEFAULT_CURRENCY).toUpperCase();
	const key = `${code}:${decimals}`;
	if (!formatters.has(key)) formatters.set(key, buildFormatter(code, decimals));
	return { code, formatter: formatters.get(key) };
};

// A non-numeric amount is rendered as zero rather than `NaN` — the dashboard
// reads plenty of optional money fields off SWR responses.
const toNumber = (amount) => Number(amount) || 0;

const format = (value, currency, decimals) => {
	const { code, formatter } = formatterFor(currency, decimals);
	// Last resort for a shop row carrying something that isn't a currency code
	// at all — show the number and the raw value rather than throwing.
	if (!formatter) return `${value.toFixed(decimals)} ${code}`;
	return formatter.format(value);
};

/**
 * Money at its natural precision — `£5` and `$1,500` when whole, `£4.50` when
 * not. This is the one to reach for on budgets and prices, where a trailing
 * `.00` is noise.
 *
 * Precision is decided from the coerced value, not the argument: `undefined`
 * formats as `£0`, not `£0.00`.
 */
export const formatCurrency = (amount, currency) => {
	const value = toNumber(amount);
	return format(value, currency, Number.isInteger(value) ? 0 : 2);
};

/**
 * Money at a fixed precision — `£29.00`, or `$0.42` for a cost-per-click.
 * Use where the decimals are part of the meaning.
 */
export const formatCurrencyExact = (amount, currency, decimals = 2) => format(toNumber(amount), currency, decimals);
